import {
    ApiCart,
    ApiShopperLandingPageLink,
    ApiLandingPageVideo,
    VLPVariant,
} from '@storefronts/common-types'

// utility type for defining type where only some keys are optional
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

export type PlaylistPlayerVideo = ApiLandingPageVideo & {
    src: string
    poster: string
    videoIndex: number
    playlistHash: string
    playlistName: string
    playerMode: string
}

export type CollectionPlayerVideo = PlaylistPlayerVideo & {
    playlistIndex: number
    collectionHash: string
    collectionName: string
}

// CART STUFF
export type SellingPlanPercentageAdjustment = {
    percentage: number
}

export type SellingPlanFixedAmountAdjustment = {
    amount: number
}

export type SellingPlanFixedPriceAdjustment = {
    price: number
}

export type SellingPlanPriceAdjustment =
    | SellingPlanFixedPriceAdjustment
    | SellingPlanPercentageAdjustment
    | SellingPlanFixedAmountAdjustment

export type SellingPlan = {
    id: string
    name: string
    description: string
    priceAdjustments: {
        adjustmentValue: SellingPlanPriceAdjustment
        // If present, represents the number of orders this price adjustment
        // applies to. Otherwise, the adjustment applies to all orders.
        orderCount?: number
    }[]
    options: {
        name: string
        value: string
    }[]
    recurringDeliveries: boolean
}

export type SellingPlanGroup = {
    id?: string
    appName: string
    name: string
    options: {
        name: string
        values: string[]
    }[]
    sellingPlansIds: string[]
}

export type SellingPlanAllocation = {
    priceAdjustments: {
        compareAtPrice?: number
        price?: number
        perDeliveryPrice?: number
        unitPrice?: number
    }[]
    checkoutChargeAmount?: number
    remainingBalanceChargeAmount?: number
    sellingPlan: SellingPlan
}

export enum CartItemStatus {
    ADDING = 'ADDING',
    UPDATING = 'UPDATING',
    REMOVING = 'REMOVING',
    READY = 'READY',
}

export type DiscountAllocation = {
    targetType: string
    discountedAmount: {
        amount: string
        currencyCode: string
    }
    code?: string
    title?: string
}

export type CartItem = {
    variant: VariantWithImage
    quantity: number
    sellingPlanId?: string | null
    availableSellingPlanAllocations?: SellingPlanAllocation[]
    sellingPlanGroups?: SellingPlanGroup[]
    shopifyLineId?: string
    status?: CartItemStatus
    attributes?: { [key: string]: string | null } | undefined
    discountAllocations?: DiscountAllocation[] | undefined
}

export type Cart = {
    id: string
    items: CartItem[]
    checkoutUrl?: string
    subtotal?: number
    total?: number
    discount?: number
    isLoading?: boolean
    attributes?: {
        [key: string]: string
    }
    discountCodes?: {
        applicable: boolean
        code: string
    }[]
}

export enum StockStatus {
    IN_STOCK = 'IN_STOCK',
    OUT_OF_STOCK = 'OUT_OF_STOCK',
    LIMITED_STOCK = 'LIMITED_STOCK',
}

export type VariantWithImage = VLPVariant & {
    imageUrl: string
    link: ApiShopperLandingPageLink
    finalPrice: number
    isSub: boolean
    stockStatus?: StockStatus
}

export function isVariantWithImage(
    variant: VariantWithImage | VLPVariant | ApiShopperLandingPageLink,
): variant is VariantWithImage {
    return 'finalPrice' in variant
}

export const PREVIEW_EVENT_ID = 'PREVIEW'

export type EventType = 'Replenishment' | 'CrossSell' | 'Recover'

export interface UserData {
    email: string
    phone_number: string
    first_name: string
    last_name: string
    address1: string
    address2: string
    city: string
    country: string
    province: string
    zip: string
}

export type ProductHistory = {
    event_type: 'ViewedProduct' | 'AddToCart' | 'InitiateCheckout' | 'Purchase'
    product_id: string
    product_title: string
    product_handle: string
    landing_page_link?: ApiShopperLandingPageLink
}

export type AbandonmentStorefrontEventData = {
    domain_name: string | undefined
    event_type: EventType | undefined
    event_id: string | undefined
    user_id: string | undefined
    site_name: string | undefined
    cart_token: string | undefined
    customer_locale?: string | undefined
    product_history: Array<ProductHistory> | []
    recover_cart_data: AbandonmentCartData
}

export type AbandonmentCartData =
    | AjaxShopifyCartData
    | ApiCart
    | undefined
    | null

export function isAjaxShopifyCartData(
    cartData: AbandonmentCartData,
): cartData is AjaxShopifyCartData {
    return !!cartData && Array.isArray((cartData as any).items)
}

export function isApiCart(cartData: AbandonmentCartData): cartData is ApiCart {
    return !!cartData && Array.isArray((cartData as any).lines)
}

export type FullAbandonmentStorefrontEventData =
    AbandonmentStorefrontEventData & {
        user_data?: UserData
    }

export type AjaxShopifyCartItem = {
    image: string | undefined
    final_price: number | undefined
    discounted_price: number | undefined
    price: number
    original_price: number
    variant_title: string
    product_title: string
    id: string | number
    product_id: string | number
    handle: string
    quantity: number
    option1?: string | undefined
    option2?: string | undefined
    option3?: string | undefined
    options?: string[] | undefined
    options_with_values?: { name: string; value: string }[] | undefined
    properties?: { [key: string]: string | null } | undefined
    featured_image?: {
        url: string | undefined
        alt?: string | undefined
        aspect_ratio?: number | undefined
        height?: number | undefined
        width?: number | undefined
    }
    selling_plan_allocation?:
        | { selling_plan: { id: string | number } }
        | undefined
}

export type AjaxShopifyCartData = {
    token: string
    items: AjaxShopifyCartItem[]
}

export type LocaleSetting = {
    locale: string | undefined
    rootRoute: string | undefined
}

export type SiteConfig = {
    name: string | undefined
    localeSettings: Array<LocaleSetting> | []
    storefrontSettings: {
        domainOverrides?: Record<string, string>
    }
}
